import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Container, Row, Col, Card, Button, Form, Spinner, Badge } from "react-bootstrap";
import { PlusCircle, XCircle, Asterisk, Check } from "react-bootstrap-icons";
import PrintItemService from "../../services/printitem.service";
import AdminService from "../../services/admin.service";
import LogoButton from "../LogoButton";
import CatalogService from "../../services/catalog.service";
import LogoService from "../../services/logo.service";
import { getSubsite } from "../../reducers/auth";

const PRICING_TYPES = { FLAT: 1, QUANTITY: 2, BILL_LATER: 3 };

const USER_PROFILE_FIELDS = [
  { value: "", label: "None (user enters manually)" },
  { value: "firstname", label: "First Name" },
  { value: "lastname", label: "Last Name" },
  { value: "fullname", label: "First Name + Last Name" },
  { value: "email", label: "Email Address" },
  { value: "phone", label: "Phone Number" },
  { value: "address", label: "Address" }
];

const STANDARD_CONTACT_PROFILE_KEYS = ["fullname", "email", "address", "phone"];

const STANDARD_CONTACT_FIELDS = [
  { name: "Name", fieldType: "text", options: [], required: false, maxLength: 200, placeholder: "Full name", userProfileField: "fullname", isEditable: false },
  { name: "Email Address", fieldType: "text", options: [], required: false, maxLength: 200, placeholder: "Email address", userProfileField: "email", isEditable: false },
  { name: "Address", fieldType: "text", options: [], required: false, maxLength: 500, placeholder: "Physical address", userProfileField: "address", isEditable: false },
  { name: "Phone Number", fieldType: "text", options: [], required: false, maxLength: 50, placeholder: "Phone number", userProfileField: "phone", isEditable: false }
];

const apiUrl = process.env.REACT_APP_LOCALHOST_URL;
const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith("http")) return url;
  if (url.includes("uploads")) return apiUrl + url;
  return url;
};

const AdminPrintItemDetails = () => {
  const { printItemId } = useParams();
  const navigate = useNavigate();
  const currentSubsite = useSelector(getSubsite);

  const [printItem, setPrintItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [personalizationFields, setPersonalizationFields] = useState([]);
  const [decoColors, setDecoColors] = useState([]);
  const [inCatalog, setInCatalog] = useState(false);
  const [isFeatured, setIsFeatured] = useState(false);
  const [addFeaturedMessage, setAddFeaturedMessage] = useState("");
  const [addFeaturedError, setAddFeaturedError] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const item = await PrintItemService.getPrintItemById(printItemId);
        setPrintItem(item);
        const catalogItems = await PrintItemService.getPrintItemsForSubsiteCatalog();
        const catalogEntry = catalogItems.find(pi => pi.id === parseInt(printItemId, 10));
        setInCatalog(!!catalogEntry?.inCatalog);
        setIsFeatured(!!catalogEntry?.featured);
      } catch (e) {
        console.error("Error loading print item:", e);
      }
      setLoading(false);
    };
    load();
  }, [printItemId]);

  useEffect(() => {
    const loadDecoColors = async () => {
      try {
        const res = await LogoService.getLogoColors();
        setDecoColors(res?.data || []);
      } catch (e) {
        console.error("Error loading decoration colors:", e);
      }
    };
    loadDecoColors();
  }, []);

  useEffect(() => {
    const loadFields = async () => {
      if (!printItem?.styleId || !currentSubsite?.id) return;
      try {
        const fields = await CatalogService.getPersonalizationFields(printItem.styleId, currentSubsite.id);
        setPersonalizationFields(fields.map(f => ({ ...f, options: f.options ? JSON.parse(f.options) : [] })));
      } catch (e) {
        console.error("Error loading personalization fields:", e);
      }
    };
    loadFields();
  }, [printItem?.styleId, currentSubsite?.id]);

  const addField = () => setPersonalizationFields([
    ...personalizationFields,
    { name: "", fieldType: "text", options: [], required: false, maxLength: 100, placeholder: "", userProfileField: "", isEditable: true, price: "", decoColorId: "" }
  ]);

  const hasStandardContactFields = STANDARD_CONTACT_PROFILE_KEYS.every(key =>
    personalizationFields.some(f => f.userProfileField === key)
  );

  const handleStandardContactFieldsToggle = async (checked) => {
    if (checked) {
      const existingKeys = new Set(
        personalizationFields.map(f => f.userProfileField).filter(Boolean)
      );
      const toAdd = STANDARD_CONTACT_FIELDS
        .filter(f => !existingKeys.has(f.userProfileField))
        .map(f => ({ ...f, options: [] }));
      if (toAdd.length > 0) {
        setPersonalizationFields([...personalizationFields, ...toAdd]);
      }
      return;
    }

    // Unchecking removes only the standard contact fields (by profile mapping).
    // Persist deletes for any already-saved fields.
    const remaining = [];
    for (let i = 0; i < personalizationFields.length; i++) {
      const field = personalizationFields[i];
      if (STANDARD_CONTACT_PROFILE_KEYS.includes(field.userProfileField)) {
        if (field.id && currentSubsite?.id) {
          try { await CatalogService.deletePersonalizationField(field.id, currentSubsite.id); }
          catch (e) { console.error("Error deleting field:", e); }
        }
      } else {
        remaining.push(field);
      }
    }
    setPersonalizationFields(remaining);
  };

  const updateField = (i, key, value) => {
    const updated = [...personalizationFields];
    updated[i][key] = value;
    setPersonalizationFields(updated);
  };

  const removeField = async (i) => {
    const field = personalizationFields[i];
    if (field.id && currentSubsite?.id) {
      try { await CatalogService.deletePersonalizationField(field.id, currentSubsite.id); }
      catch (e) { console.error("Error deleting field:", e); }
    }
    setPersonalizationFields(personalizationFields.filter((_, idx) => idx !== i));
  };

  const addOption = (i) => updateField(i, "options", [...(personalizationFields[i].options || []), ""]);
  const updateOption = (i, oi, value) => {
    const opts = [...(personalizationFields[i].options || [])];
    opts[oi] = value;
    updateField(i, "options", opts);
  };
  const removeOption = (i, oi) => updateField(i, "options", personalizationFields[i].options.filter((_, idx) => idx !== oi));

  const saveFields = async () => {
    if (!printItem?.styleId) { alert("This print item has no style assigned; personalization cannot be saved."); return; }
    if (!currentSubsite?.id) { alert("No subsite selected."); return; }
    try {
      await CatalogService.savePersonalizationFields(printItem.styleId, personalizationFields, currentSubsite.id);
      alert("Personalization fields saved for " + currentSubsite.subsiteName + ".");
    } catch (e) {
      console.error("Error saving personalization fields:", e);
      alert("Error saving personalization fields");
    }
  };

  const setAsFeatured = async () => {
    const status = await AdminService.addPrintItemFeatured(printItemId);
    if (status && status.error) {
      setAddFeaturedError(true);
      setAddFeaturedMessage(status.message);
    } else {
      setAddFeaturedError(false);
      setAddFeaturedMessage("Set as Featured");
      setIsFeatured(true);
    }
  };

  if (loading) return <Spinner />;
  if (!printItem) return <div className="p-3">Print item not found.</div>;

  return (
    <Container className="mt-3">
      <Button variant="link" onClick={() => navigate(-1)}>&larr; Back</Button>
      <Row>
        <Col md={4}>
          {printItem.imageUrl && <img src={getImageUrl(printItem.imageUrl)} alt={printItem.title} style={{ maxWidth: "100%" }} />}
        </Col>
        <Col md={8}>
          <h2>{printItem.title}</h2>
          {printItem.code && <div className="text-muted">{printItem.code}</div>}
          <div className="mt-2" dangerouslySetInnerHTML={{ __html: printItem.htmlDescription || printItem.description || "" }} />
          <div className="mt-2"><strong>Pricing: </strong>
            {printItem.pricingType === PRICING_TYPES.BILL_LATER ? "Billed Later"
              : printItem.pricingType === PRICING_TYPES.QUANTITY ? "Quantity-based"
              : `$${(printItem.price || 0).toFixed(2)}`}
          </div>
          {!printItem.styleId && (
            <div className="alert alert-warning mt-3">
              No style assigned to this print item. Assign one in the global Print Item form to enable personalization.
            </div>
          )}
          {!inCatalog && (
            <div className="alert alert-warning mt-3">
              This print item is not in the current subsite catalog. Add it from the Print Items page before setting it as featured.
            </div>
          )}
          {inCatalog && (
            <div className="mt-3">
              <span style={{ fontWeight: "bold", color: "#999999" }}>ACTIONS:</span>
              <ul>
                <li>
                  Set As Featured{" "}
                  <LogoButton style={{ color: "blue" }} size="25" Icon={Asterisk} tooltip="Set as Featured" onClick={setAsFeatured} />
                  {isFeatured && <Badge bg="success" style={{ marginLeft: 8 }}>Featured</Badge>}
                  {addFeaturedMessage && (
                    <>
                      <br />
                      <div style={{ color: addFeaturedError ? "#cc0000" : "#009900" }}>
                        {!addFeaturedError && <Check size="20" />}
                        {addFeaturedMessage}
                      </div>
                    </>
                  )}
                </li>
              </ul>
            </div>
          )}
        </Col>
      </Row>

      <hr />

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
        <span style={{ fontWeight: "bold", color: "#999999" }}>
          PERSONALIZATION FIELDS
          {currentSubsite && <Badge bg="secondary" style={{ marginLeft: 8, fontWeight: "normal" }}>{currentSubsite.subsiteName}</Badge>}
          {personalizationFields.length > 0 && <Badge bg="info" style={{ marginLeft: 8 }}>{personalizationFields.length}</Badge>}
        </span>
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <Form.Check
            type="checkbox"
            id="standard-contact-fields"
            label="Add name, email, address & phone"
            checked={hasStandardContactFields}
            disabled={!printItem.styleId}
            onChange={(e) => handleStandardContactFieldsToggle(e.target.checked)}
          />
          <Button variant="outline-primary" size="sm" onClick={addField} disabled={!printItem.styleId}>
            <PlusCircle style={{ marginRight: 5 }} /> Add Field
          </Button>
        </div>
      </div>
      <small style={{ color: "#666", display: "block", marginBottom: 10 }}>
        Configure text fields or dropdowns for <strong>{currentSubsite?.subsiteName || "this subsite"}</strong>. Each subsite can have different personalization fields.
        Check the box to automatically add Name, Email, Address, and Phone (pre-filled from the user profile); you can edit or remove them afterward.
      </small>

      {personalizationFields.map((field, fi) => (
        <Card key={fi} style={{ marginBottom: 10, padding: 10 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <div style={{ flex: 1 }}>
              <Row>
                <Col xs={4}>
                  <Form.Label>Field Name</Form.Label>
                  <Form.Control type="text" placeholder="e.g., Employee Name" value={field.name}
                    onChange={(e) => updateField(fi, "name", e.target.value)} />
                </Col>
                <Col xs={4}>
                  <Form.Label>Type</Form.Label>
                  <Form.Select value={field.fieldType} onChange={(e) => updateField(fi, "fieldType", e.target.value)}>
                    <option value="text">Text</option>
                    <option value="select">Dropdown</option>
                  </Form.Select>
                </Col>
                <Col xs={2}>
                  <Form.Label>Required</Form.Label>
                  <Form.Check type="checkbox" checked={!!field.required}
                    onChange={(e) => updateField(fi, "required", e.target.checked)} />
                </Col>
              </Row>
              {field.fieldType === "text" && (
                <Row className="mt-2">
                  <Col xs={8}>
                    <Form.Label>Placeholder</Form.Label>
                    <Form.Control type="text" value={field.placeholder || ""}
                      onChange={(e) => updateField(fi, "placeholder", e.target.value)} />
                  </Col>
                  <Col xs={4}>
                    <Form.Label>Max Length</Form.Label>
                    <Form.Control type="number" value={field.maxLength || ""}
                      onChange={(e) => updateField(fi, "maxLength", parseInt(e.target.value) || null)} />
                  </Col>
                </Row>
              )}
              {field.fieldType === "select" && (
                <div style={{ marginTop: 10 }}>
                  <Form.Label>Dropdown Options</Form.Label>
                  {(field.options || []).map((option, oi) => (
                    <div key={oi} style={{ display: "flex", marginBottom: 5 }}>
                      <Form.Control type="text" placeholder={`Option ${oi + 1}`} value={option}
                        onChange={(e) => updateOption(fi, oi, e.target.value)} style={{ marginRight: 5 }} />
                      <Button variant="outline-danger" size="sm" onClick={() => removeOption(fi, oi)}><XCircle /></Button>
                    </div>
                  ))}
                  <Button variant="outline-secondary" size="sm" onClick={() => addOption(fi)}><PlusCircle /> Add Option</Button>
                </div>
              )}
              <Row className="mt-2">
                <Col xs={6}>
                  <Form.Label>Pre-fill from User Profile</Form.Label>
                  <Form.Select value={field.userProfileField || ""}
                    onChange={(e) => updateField(fi, "userProfileField", e.target.value)}>
                    {USER_PROFILE_FIELDS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </Form.Select>
                </Col>
                <Col xs={6} style={{ display: "flex", alignItems: "flex-end" }}>
                  <Form.Check type="checkbox" label="Editable by user"
                    checked={field.isEditable !== false}
                    disabled={!field.userProfileField}
                    onChange={(e) => updateField(fi, "isEditable", e.target.checked)} />
                </Col>
              </Row>
              <Row className="mt-2">
                <Col xs={4}>
                  <Form.Label>Price (optional)</Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                    value={field.price ?? ""}
                    onChange={(e) => updateField(fi, "price", e.target.value === "" ? "" : e.target.value)}
                  />
                </Col>
                <Col xs={6}>
                  <Form.Label>Decoration Color (optional)</Form.Label>
                  <Form.Select
                    value={field.decoColorId || ""}
                    onChange={(e) => updateField(fi, "decoColorId", e.target.value === "" ? "" : parseInt(e.target.value, 10))}
                  >
                    <option value="">— None —</option>
                    {decoColors.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.colorName}{c.thread ? ` (thread ${c.thread})` : ""}{c.pms ? ` · PMS ${c.pms}` : ""}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
              </Row>
            </div>
            <Button variant="outline-danger" size="sm" onClick={() => removeField(fi)} style={{ marginLeft: 10 }}>
              <XCircle />
            </Button>
          </div>
        </Card>
      ))}

      {personalizationFields.length > 0 && (
        <Button variant="success" size="sm" onClick={saveFields} style={{ marginTop: 10 }}>
          Save Personalization Fields
        </Button>
      )}
    </Container>
  );
};

export default AdminPrintItemDetails;
